import { BrowserModule, HammerGestureConfig, HAMMER_GESTURE_CONFIG, HammerModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { NgModule, APP_INITIALIZER, Injectable } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HttpClientModule } from '@angular/common/http';
import { WephoneTranslationModule } from '@wephone-translation';
import { FlexLayoutModule } from '@angular/flex-layout';
import { BidiModule } from '@angular/cdk/bidi';
import { WephoneAudioModule } from '@wephone-audio';
import { APP_BASE_HREF } from '@angular/common';

import {
  MenuComponent,
  PhoneHeaderComponent,
  SidebarComponent,
  NotificationComponent,
  PhoneLayoutComponent,
  AuthLayoutComponent
} from './core';

import {
  PhoneService,
  HistoryService,
  SipAccountService,
  PhoneConfigService,
  ServiceWorkerService,
  ScreenLockService
} from '@wephone-app-phone/services';

import { AppRoutes, RoutingService } from './app.routing';
import { AppComponent } from './app.component';
import { CookieService as NgxCookieService } from 'ngx-cookie-service';
import { WephoneUtilsModule, CookieService, DialogService } from '@wephone-utils';
import { LayoutModule } from '@angular/cdk/layout';
import { WephoneCoreModule, FlexIvrSettings, ConfigManager } from '@wephone-core/wephone-core.module';
import { DialerPage } from '@wephone-app-phone/dialer/dialer/dialer';
import { JSPhoneStatus } from '@wephone-app-phone/components/jsphone-status/jsphone-status';
import { KeypadComponent } from '@wephone-app-phone/components/keypad/keypad';
import { UserCommonSettingsComponent } from '@wephone-app-phone/modals/settings/user-common-settings/user-common-settings';
import { AgentPageComponent } from '@wephone-app-phone/pages/agent-page/agent-page.component';
import { AgentPanelComponent } from '@wephone-app-phone/components/agent-panel/agent-panel';
import { Phonebook } from '@wephone-app-phone/components/phone-book/phone-book';
import { CallLogListPage } from '@wephone-app-phone/pages/history/calllog-list/calllog-list';
import { EnterpriseContactsPage } from '@wephone-app-phone/pages/enterprise-contacts/enterprise-contacts';
import { MyActiveCallsComponent } from '@wephone-app-phone/call/my-active-calls/my-active-calls';
import { CallStandbyComponent } from '@wephone-app-phone/call/call-standby/call-standby';
import { CallActiveComponent } from '@wephone-app-phone/call/call-active/call-active';
import { CallTransferModal } from '@wephone-app-phone/modals/call-transfer/call-transfer';
import { PhoneDashboardFooterComponent } from '@wephone-app-phone/core/phone-dashboard-footer/phone-dashboard-footer.component';
import { WephoneCommonModule } from '@wephone-common';
import { NgxElectronModule } from 'ngx-electron';
import { KeychainTouchId } from '@ionic-native/keychain-touch-id/ngx/';
import { AndroidPermissions } from '@ionic-native/android-permissions/ngx/';
import { UserProfileService } from '@wephone/services/user-profile.service';
import { ValidatorService } from '@wephone-utils/services/validator.service';
import { UserService } from '@wephone/services/user.service';
import { RepositoryService } from '@wephone-core/service/repository_service';
import { ActiveCallInfoComponent } from '@wephone-app-phone/components/active-call-info/active-call-info.component';
import { DataBuilderService } from '@wephone/services/data-builder.service';
import { AgentPanelSettingComponent } from '@wephone-app-phone/modals/settings/agent-panel-settings/agent-panel-settings';
import { NoAudioDeviceWarnDialog } from '@wephone-app-phone/modals/no-audio-device-warn/no-audio-device-warn';
import { PhoneDashboardPage } from '@wephone-app-phone/pages/phone-dashboard/phone-dashboard.component';
import { AgentService } from '@wephone/services/agent.service';
import { AgentPanelService } from '@wephone/services/agent-panel.service';
import { WephoneCoreUIModule } from '@wephone-core-ui';
import { RequestResetPwDialogComponent } from '@wephone/modals/request-reset-pw-dialog/request-reset-pw-dialog.component';
import { TestHeadsetModalComponent } from './modals/test-headset-modal/test-headset-modal.component';
import { OutcallNextTasks } from '@wephone/components/outcall-next-tasks/outcall-next-tasks.component';
import { BottomSheetChangeVolume } from '@wephone-app-phone/modals/settings/bottom-sheet-change-volume/bottom-sheet-change-volume.component';
import { WephoneSentryModule } from 'src/app/wephone-common/wephone-sentry.module';
import { CallActiveMinimizedComponent } from './call/call-active-minimized/call-active-minimized';
import { MatFormFieldDefaultOptions, MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { CallCampaignRescheduleModal } from './modals/call-campaign-reschedule/call-campaign-reschedule';
import { AudioDeviceService } from './services/audio-devices.service';
import { AudioDeviceNotificationComponent } from './components/audio-device-notification/audio-device-notification.component';
import { CallHistoryComponent } from './pages/call-history/call-history.component';
import { MissedCallComponent } from './pages/missed-call/missed-call.component';
import { SalesforceService } from './services/phone/salesforce.service';
import { HubspotService } from './services/phone/hubspot.service';
import { StorageService } from '@wephone-core/service/storage.service';
import { CallDestinationService } from '@wephone/services/call-destination.service';
import { ServiceWorkerModule } from '@angular/service-worker';
import { environment } from 'src/environments/environment';
import { TodayPerformanceComponent } from '@wephone/components/today-performance/today-performance.component';
import { ChangeUserPasswordModal } from '@wephone/modals/change-user-password/change-user-password';
import { EnterpriseContactsModalComponent } from './modals/enterprise-contacts-modal/enterprise-contacts-modal';
import { CrmIncallActionsComponent } from './components/crm-incall-actions/crm-incall-actions.component';
import { UpdateCrmContactModalComponent } from './modals/update-crm-contact-modal/update-crm-contact-modal.component';
import { CrmCreateTaskModalComponent } from './modals/crm-create-task-modal/crm-create-task-modal.component';
import { WephoneSMSModule } from 'src/app/wephone-sms/wephone-sms.module';
import { WephoneCustomIconModule } from '@wephone-custom-icon';
import { CrmContactHistoryComponent } from './components/crm-contact-history/crm-contact-history.component';
import { AddCreditCardComponent } from '@wephone/modals/add-credit-card/add-credit-card.component';
import { AddSepaDebitComponent } from '@wephone/modals/add-sepa-debit/add-sepa-debit.component';
import { UpdatePaymentMethodModal } from '@wephone/modals/update-payment-method/update-payment-method.component';
import { PaymentService } from '@wephone/services/payment.service';
import { SubscriptionService } from '@wephone/services/subscription.service';
import { RatingModule } from 'ng-starrating';
import { CallEvaluationService } from '@wephone-app-phone/services/call-evaluation.service';
import { BottomSheetCallQualityEvaluationComponent } from './modals/bottom-sheet-call-quality-evaluation/bottom-sheet-call-quality-evaluation.component';
import { MyvoicemailsComponent } from './pages/voicemails/myvoicemails/myvoicemails.component';
import { IPPhoneProvisioningModal } from './modals/ipphone-provisioning/ipphone-provisioning';
import { GrandStreamProvisioningService } from './services/phone-provisioning';
import { IssueReportModalComponent } from './modals/issue-report/issue-report-modal.component';
import { IssueReportService } from './services/issue-report.service';
import { TransferConfirmModal } from './modals/transfer-confirm/transfer-confirm';
import { QuickDialerComponent } from './dialer/quick-dialer/quick-dialer.component';
import { TodayStatsModalComponent } from './modals/today-stats-modal/today-stats-modal.component';
import { LiveTrackingModalComponent } from './modals/live-tracking-modal/live-tracking-modal';
import { SmsComponent } from './pages/sms/sms.component';
import { RecordingCallAnalysisResultComponent } from '@wephone/modals/recording-call-analysis-result/recording-call-analysis-result.component';
import { PhoneRepositoryService } from './services/phone-repository.service';

FlexIvrSettings.getInstance().update({
  ws2_url: '/ws-v2/',
  inside_salesforce: !!window['sforce']
}).setProjectPhone();

const formFieldAppearance: MatFormFieldDefaultOptions = {
  appearance: 'standard',
  floatLabel: 'always'
};

@Injectable()
export class HammerConfig extends HammerGestureConfig {
  overrides = {
    pinch: { enable: false },
    rotate: { enable: false }
  }
}

export function init_salesforce(sf: SalesforceService, hs: HubspotService, storage: StorageService) {
  return async () => {
    const insideSalesforce = new URL(location.href).searchParams.get('salesforce');
    const insideHubspot = new URL(location.href).searchParams.get('hubspot');
    window['GRAAM_IN_IFRAME'] = insideSalesforce || insideHubspot;

    if (insideSalesforce === 'true') {
      await sf.loadCTIScript();
    } else if (insideHubspot === 'true') {
      hs.initialized();
    }
    // Create configuration for hotkey if needed
    let hotkeyConfig = await storage.getAppConfig('hotkey_config');
    const platform = window['ELECTRON_PLATFORM'];
    if (!hotkeyConfig) {
      hotkeyConfig = {
        status: true,
        instant_dial: true,
        meta_key: platform === 'darwin' ? 'command' : 'control',
        event_key: 'D',
      };

      await storage.setAppConfig('hotkey_config', hotkeyConfig);
    }
  };
}

@NgModule({
  declarations: [
    AppComponent,
    SidebarComponent,
    NotificationComponent,
    MenuComponent,
    PhoneLayoutComponent,
    AuthLayoutComponent,
    DialerPage,
    PhoneHeaderComponent,
    PhoneDashboardFooterComponent,
    JSPhoneStatus,
    KeypadComponent,
    UserCommonSettingsComponent,
    TestHeadsetModalComponent,
    RequestResetPwDialogComponent,
    AgentPanelSettingComponent,
    AgentPageComponent,
    AgentPanelComponent,
    Phonebook,
    CallLogListPage,
    // CallLogDetail,
    EnterpriseContactsPage,
    MyActiveCallsComponent,
    CallStandbyComponent,
    CallActiveComponent,
    CallTransferModal,
    ActiveCallInfoComponent,
    MyvoicemailsComponent,
    NoAudioDeviceWarnDialog,
    PhoneDashboardPage,
    OutcallNextTasks,
    BottomSheetChangeVolume,
    CallActiveMinimizedComponent,
    CallCampaignRescheduleModal,
    AudioDeviceNotificationComponent,
    CallHistoryComponent,
    MissedCallComponent,
    TodayPerformanceComponent,
    ChangeUserPasswordModal,
    UpdatePaymentMethodModal,
    AddCreditCardComponent,
    AddSepaDebitComponent,
    EnterpriseContactsModalComponent,
    CrmIncallActionsComponent,
    UpdateCrmContactModalComponent,
    CrmCreateTaskModalComponent,
    CrmContactHistoryComponent,
    BottomSheetCallQualityEvaluationComponent,
    IPPhoneProvisioningModal,
    IssueReportModalComponent,
    TransferConfirmModal,
    QuickDialerComponent,
    TodayStatsModalComponent,
    LiveTrackingModalComponent,
    SmsComponent,
    RecordingCallAnalysisResultComponent
  ],
  entryComponents: [
    UserCommonSettingsComponent,
    TestHeadsetModalComponent,
    RequestResetPwDialogComponent,
    // CallLogDetail,
    CallHistoryComponent,
    MissedCallComponent,
    Phonebook,
    MyActiveCallsComponent,
    CallTransferModal,
    ActiveCallInfoComponent,
    MyvoicemailsComponent,
    NoAudioDeviceWarnDialog,
    BottomSheetChangeVolume,
    CallActiveMinimizedComponent,
    CallCampaignRescheduleModal,
    AudioDeviceNotificationComponent,
    TodayPerformanceComponent,
    ChangeUserPasswordModal,
    UpdatePaymentMethodModal,
    AddCreditCardComponent,
    AddSepaDebitComponent,
    EnterpriseContactsModalComponent,
    UpdateCrmContactModalComponent,
    CrmCreateTaskModalComponent,
    BottomSheetCallQualityEvaluationComponent,
    IPPhoneProvisioningModal,
    IssueReportModalComponent,
    TransferConfirmModal,
    QuickDialerComponent,
    TodayStatsModalComponent,
    LiveTrackingModalComponent,
    SmsComponent,
    RecordingCallAnalysisResultComponent
  ],
  imports: [
    WephoneSentryModule.forRoot(),
    WephoneSMSModule.forRoot(),
    BrowserModule,
    BrowserAnimationsModule,
    HammerModule,
    RouterModule.forRoot(AppRoutes, { useHash: true, relativeLinkResolution: 'legacy' }),
    FormsModule,
    HttpClientModule,
    WephoneTranslationModule.forRoot(),
    WephoneUtilsModule.forRoot(),
    WephoneCoreModule.forRoot(),
    WephoneCoreUIModule.forRoot(),
    WephoneCommonModule.forRoot(),
    WephoneAudioModule.forRoot(),
    LayoutModule,
    FlexLayoutModule,
    NgxElectronModule,
    BidiModule,
    WephoneCustomIconModule,
    ServiceWorkerModule.register('./phone-service-worker.js', { enabled: true }),
    RatingModule,
  ],
  providers: [
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: formFieldAppearance
    },
    {
      provide: HAMMER_GESTURE_CONFIG,
      useClass: HammerConfig
    },
    {
      provide: APP_BASE_HREF,
      useValue: window['_app_base'] || '/phone'
    },
    PhoneConfigService,
    SalesforceService,
    HubspotService,
    { provide: APP_INITIALIZER, useFactory: init_salesforce, deps: [SalesforceService, HubspotService, StorageService], multi: true },
    RoutingService,
    NgxCookieService,
    CookieService,
    PhoneService,
    AgentService,
    AgentPanelService,
    UserProfileService,
    ValidatorService,
    HistoryService,
    SipAccountService,
    KeychainTouchId,
    AndroidPermissions,
    UserService,
    {
      provide: RepositoryService,
      useClass: PhoneRepositoryService
    },
    // RepositoryService,
    DataBuilderService,
    AudioDeviceService,
    CallDestinationService,
    PaymentService,
    SubscriptionService,
    CallEvaluationService,
    GrandStreamProvisioningService,
    ServiceWorkerService,
    ScreenLockService,
    IssueReportService,
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
  constructor(cookieService: CookieService, settings: FlexIvrSettings, config: ConfigManager, grandStreamProvisioningService: GrandStreamProvisioningService, dialog: DialogService) {
    cookieService.app_root_url = settings.root_url;
    grandStreamProvisioningService.startListening();
    // dialog.setForcedPannelClass('wephone-dialog-panel-phoneapp');
    // config.setRemoteUrl(settings.getApiUrl('system/config/get_config'));
    // config.setRemoteUrl(settings.getAbsoluteApiUrlv2('system/config/get_config'));
  }
}
